import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Package, Truck, Clock, XCircle, CheckCircle2, Boxes, Loader2, Split, Archive } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

// Phase 2.7 · 합포/부분출고/입고대기 관리 모달
// - Per-item fulfillment_status (ready · waiting_stock · shipped · out_of_stock · cancelled)
// - shipment_mode: hold_all vs partial_ship
// - Create partial shipments (multiple shipments per single order)
// - Real 재고차감: shipped_qty 만큼만 (idempotent)
// - 배송비: mock 필드만 (실 청구 없음, seller_bears_cost 기본)

const ITEM_STATUS_LABEL = {
  ready: '출고준비',
  waiting_stock: '입고대기',
  hold: '출고보류',
  shipped: '출고완료',
  out_of_stock: '품절',
  cancelled: '취소',
};
const ITEM_STATUS_CLS = {
  ready:         'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
  waiting_stock: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  hold:          'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300',
  shipped:       'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  out_of_stock:  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300',
  cancelled:     'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:border-white/10',
};

const COURIERS = ['CJ대한통운', '롯데택배', '한진택배', '우체국택배', '로젠택배', '경동택배'];
const fmt = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };
const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// STEP OMS-PARTIAL-PICK-1A · 상품별 고유 key.
// - seller_order_item_id 우선.  없거나 중복될 때만 (product_id::option::index) 폴백.
// - React 렌더 key + 수량/체크 state 는 반드시 같은 값으로 통일 (충돌 방지).
const _optionSig = (opt) => {
  if (!opt || typeof opt !== 'object') return typeof opt === 'string' ? opt : '';
  return Object.entries(opt)
    .filter(([, v]) => v !== null && v !== '')
    .map(([k, v]) => `${k}=${v}`).sort().join('|');
};
const _stableKey = (it, i) => {
  const iid = (it?.seller_order_item_id || '').trim();
  if (iid) return iid;
  return `${it?.product_id || 'p'}::${_optionSig(it?.option) || 'default'}::${i}`;
};
const _renderOptionText = (opt) => {
  if (!opt || typeof opt !== 'object') return typeof opt === 'string' ? opt : '';
  return Object.entries(opt).filter(([, v]) => v !== null && v !== '')
    .map(([, v]) => v).join(' / ');
};

export default function PartialShipmentModal({ open, sellerOrderId, orderNumber, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // STEP OMS-PARTIAL-PICK-1A · state 를 두 map 으로 분리 · 상품별 독립.
  //  - picked : {stableKey → bool} 체크박스
  //  - qty    : {stableKey → number} 이번 출고수량 (bounded 1..pending_qty)
  const [picked, setPicked] = useState({});
  const [qty, setQty] = useState({});
  const [courier, setCourier] = useState('CJ대한통운');
  const [tracking, setTracking] = useState('');
  const [feeKrw, setFeeKrw] = useState(3000);
  const [creatingShip, setCreatingShip] = useState(false);
  const [busyMode, setBusyMode] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/seller/fulfillment/${sellerOrderId}/items`);
      setData(r.data);
      // Preselect all ready items with full pending qty (독립 map)
      const p = {}; const q = {};
      (r.data.items || []).forEach((it, i) => {
        const k = _stableKey(it, i);
        if (it.fulfillment_status === 'ready' && (it.pending_qty || 0) > 0) {
          p[k] = true;
          q[k] = it.pending_qty;
        }
      });
      setPicked(p); setQty(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '불러오기 실패');
      onClose?.();
    } finally { setLoading(false); }
  }, [sellerOrderId, onClose]);

  useEffect(() => { if (open && sellerOrderId) load(); }, [open, sellerOrderId, load]);

  if (!open) return null;

  const items = data?.items || [];
  const summary = data?.summary || {};
  const shipments = data?.shipments || [];
  const currentMode = data?.shipment_mode || 'hold_all';

  const setMode = async (mode) => {
    setBusyMode(true);
    try {
      const r = await api.post(`/seller/fulfillment/${sellerOrderId}/shipment-mode`, { mode });
      toast.success(`모드 변경: ${r.data.label}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusyMode(false); }
  };

  const saveItemStatus = async () => {
    if (!editItem) return;
    try {
      await api.post(`/seller/fulfillment/${sellerOrderId}/item/status`, {
        seller_order_item_id: editItem.item.seller_order_item_id,
        product_id: editItem.item.product_id,
        option: editItem.item.option || {},
        status: editItem.newStatus,
        eta_date: editItem.eta || undefined,
      });
      toast.success('상품 상태가 변경되었습니다.');
      setEditItem(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
  };

  const totalSelectedQty = (data?.items || []).reduce((sum, it, i) => {
    const k = _stableKey(it, i);
    if (!picked[k]) return sum;
    return sum + (Number(qty[k]) || 0);
  }, 0);

  const createShipment = async () => {
    if (totalSelectedQty <= 0) { toast.error('출고할 상품을 선택해주세요.'); return; }
    if (!tracking.trim()) { toast.error('송장번호를 입력해주세요.'); return; }
    setCreatingShip(true);
    try {
      // STEP OMS-PARTIAL-PICK-1A · 체크된 ready item + 상품별 독립 수량만 item_refs 로 전송.
      const item_refs = (data?.items || [])
        .map((it, i) => ({ it, i, k: _stableKey(it, i) }))
        .filter(({ it, k }) => picked[k] && it.fulfillment_status === 'ready' && (Number(qty[k]) || 0) > 0)
        .map(({ it, k }) => ({
          seller_order_item_id: it.seller_order_item_id,
          product_id: it.product_id,
          option: it.option || {},
          quantity: Number(qty[k]) || 0,
        }));
      const r = await api.post(`/seller/fulfillment/${sellerOrderId}/shipments`, {
        item_refs, courier, tracking_number: tracking.trim(),
        estimated_shipping_fee: Number(feeKrw) || 0,
        cost_bearer: 'seller_bears_cost',
      });
      toast.success(`shipment #${r.data.shipment.shipment_no} 생성 (${r.data.is_partial ? '부분출고' : '전체출고'})`);
      setTracking('');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setCreatingShip(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="partial-shipment-modal">
      <div className="flex-1 bg-black/50" onClick={onClose}/>
      <div className="w-full max-w-2xl bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-[#0A0A0A] border-b border-black/10 dark:border-white/10 p-3 flex justify-between items-center z-10">
          <div>
            <p className="text-sm font-black flex items-center gap-1"><Split size={14}/> 합포·부분출고 관리</p>
            <p className="text-[11px] text-gray-500 font-mono">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="ps-close"><X size={16}/></button>
        </div>

        {loading ? (
          <div className="py-24 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Summary */}
            <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.02] dark:bg-white/[0.03]" data-testid="ps-summary">
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">진행 요약</p>
              <p className="text-sm font-bold">{summary.label || '-'}</p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <SumChip label="준비" v={summary.ready_qty || 0} cls={ITEM_STATUS_CLS.ready} tid="ps-sum-ready"/>
                <SumChip label="대기" v={summary.waiting_qty || 0} cls={ITEM_STATUS_CLS.waiting_stock} tid="ps-sum-waiting"/>
                <SumChip label="출고" v={summary.shipped_qty || 0} cls={ITEM_STATUS_CLS.shipped} tid="ps-sum-shipped"/>
                <SumChip label="전체" v={summary.total_qty || 0} cls={'bg-black/5 border-black/10 text-black dark:bg-white/5 dark:border-white/10 dark:text-white'} tid="ps-sum-total"/>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="rounded-xl border-2 border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 space-y-2" data-testid="ps-mode">
              <p className="text-[10px] font-black uppercase text-[#FF3B30] flex items-center gap-1"><Boxes size={12}/> 출고 모드</p>
              <div className="grid grid-cols-2 gap-2">
                <button disabled={busyMode || currentMode === 'hold_all'}
                  onClick={() => setMode('hold_all')} data-testid="ps-mode-hold-all"
                  className={`px-3 py-2 rounded-lg border text-xs font-black text-left ${currentMode === 'hold_all' ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'}`}>
                  <Archive size={11} className="inline mr-1"/> 전체상품 입고까지 대기
                  <p className="text-[10px] font-normal mt-1 opacity-70">준비된 상품이 있어도 출고하지 않습니다 (합포 대기)</p>
                </button>
                <button disabled={busyMode || currentMode === 'partial_ship'}
                  onClick={() => setMode('partial_ship')} data-testid="ps-mode-partial-ship"
                  className={`px-3 py-2 rounded-lg border text-xs font-black text-left ${currentMode === 'partial_ship' ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'}`}>
                  <Split size={11} className="inline mr-1"/> 준비상품 먼저 선발송
                  <p className="text-[10px] font-normal mt-1 opacity-70">준비완료된 상품을 부분출고 할 수 있습니다</p>
                </button>
              </div>
            </div>

            {/* Item list */}
            <div className="rounded-xl border border-black/10 dark:border-white/10" data-testid="ps-items">
              <div className="p-2 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
                <p className="text-[10px] font-black uppercase text-gray-500">주문 상품별 상태</p>
                <p className="text-[10px] text-gray-500">{items.length}건</p>
              </div>
              {items.map((it, i) => {
                const key = _stableKey(it, i);
                const s = it.fulfillment_status;
                const isReady = s === 'ready' && (it.pending_qty || 0) > 0;
                const modeAllowsSelect = currentMode === 'partial_ship';
                const isChecked = !!picked[key];
                const rawQty = qty[key];
                // ── 수량 클램프 (표시용) · 1 ≤ v ≤ pending_qty ──
                const clampedQty = Math.max(1, Math.min(it.pending_qty || 1, Number(rawQty) || 0));
                const optionText = _renderOptionText(it.option);
                return (
                  <div key={key} className={`p-3 border-b border-black/5 dark:border-white/10 last:border-b-0 ${isChecked && isReady && modeAllowsSelect ? 'bg-[#FF3B30]/5' : ''}`}
                       data-testid={`ps-item-${key}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        {/* STEP OMS-PARTIAL-PICK-1A · 상품별 체크박스 */}
                        <input type="checkbox" className="mt-1 accent-[#FF3B30] flex-shrink-0"
                               checked={isChecked}
                               disabled={!isReady || !modeAllowsSelect}
                               onChange={(e) => {
                                 const on = e.target.checked;
                                 setPicked((prev) => ({ ...prev, [key]: on }));
                                 // 체크 시 수량 default: 이전 값 유지 or pending_qty
                                 if (on) {
                                   setQty((prev) => ({
                                     ...prev,
                                     [key]: Math.max(1, Math.min(it.pending_qty || 1, Number(prev[key]) || it.pending_qty || 1)),
                                   }));
                                 }
                               }}
                               data-testid={`ps-item-check-${key}`}
                               title={!modeAllowsSelect
                                        ? '[준비상품 먼저 선발송] 모드에서만 선택할 수 있습니다.'
                                        : !isReady ? '출고준비 상품만 선택할 수 있습니다.' : undefined}/>
                        <div className="min-w-0">
                          <p className="text-sm font-bold truncate">{it.name_snapshot}</p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {optionText || '기본옵션'}
                            {it.sku && <span className="ml-1 font-mono">· SKU {it.sku}</span>}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black shrink-0 ${ITEM_STATUS_CLS[s] || ITEM_STATUS_CLS.ready}`} data-testid={`ps-item-status-${key}`}>
                        {ITEM_STATUS_LABEL[s] || s}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="text-gray-500">
                        주문수량 <b className="text-black dark:text-white">{it.quantity}</b>
                        {it.shipped_qty > 0 && <span className="ml-2 text-emerald-600">· 출고 {it.shipped_qty}</span>}
                        {it.pending_qty > 0 && s !== 'shipped' && <span className="ml-2 text-[#FF3B30]">· 미출고 {it.pending_qty}</span>}
                        {it.eta_date && <span className="ml-2 text-amber-600">· ETA {it.eta_date}</span>}
                        {it.expected_stock > 0 && s === 'waiting_stock' && (
                          <span className="ml-2 text-indigo-600 dark:text-indigo-300" data-testid={`ps-item-expected-${key}`}>· 입고예정 {it.expected_stock}개</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {s !== 'shipped' && s !== 'cancelled' && (
                          <button onClick={() => setEditItem({ item: it, newStatus: s === 'ready' ? 'waiting_stock' : 'ready', eta: it.eta_date || '' })}
                            className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[10px] font-bold" data-testid={`ps-item-edit-${key}`}>
                            상태 변경
                          </button>
                        )}
                      </div>
                    </div>
                    {isReady && modeAllowsSelect && isChecked && (
                      <div className="mt-2 flex items-center gap-2" data-testid={`ps-item-select-${key}`}>
                        <label className="text-[11px] text-gray-500">이번 출고수량</label>
                        <input type="number" min={1} max={it.pending_qty}
                          value={clampedQty}
                          onChange={(e) => {
                            const raw = Number(e.target.value);
                            const safe = Number.isFinite(raw)
                              ? Math.max(1, Math.min(it.pending_qty || 1, Math.floor(raw)))
                              : 1;
                            // STEP OMS-PARTIAL-PICK-1A · 상품별 독립 수량 · key 로만 write.
                            setQty((prev) => ({ ...prev, [key]: safe }));
                          }}
                          className="w-20 px-2 py-1 text-xs border border-black/10 dark:border-white/10 rounded bg-transparent"
                          data-testid={`ps-item-qty-${key}`}/>
                        <span className="text-[11px] text-gray-500">/ {it.pending_qty}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Existing shipments */}
            {shipments.length > 0 && (
              <div className="rounded-xl border border-black/10 dark:border-white/10" data-testid="ps-shipments">
                <div className="p-2 border-b border-black/10 dark:border-white/10">
                  <p className="text-[10px] font-black uppercase text-gray-500">발송 이력 ({shipments.length}건)</p>
                </div>
                {shipments.map((sh) => (
                  <div key={sh.shipment_id} className="p-3 border-b border-black/5 dark:border-white/10 last:border-b-0" data-testid={`ps-shipment-${sh.shipment_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-black flex items-center gap-1"><Truck size={12}/> #{sh.shipment_no} · {sh.courier}</p>
                      <p className="text-[10px] text-gray-500">{fmt(sh.shipped_at)}</p>
                    </div>
                    <p className="text-[11px] font-mono">{sh.tracking_number}</p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      상품 {sh.item_refs?.length || 0}종 · 배송비 {won(sh.estimated_shipping_fee)} <span className="text-amber-600">(mock · 판매자 부담)</span>
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {(sh.item_refs || []).map((r, i) => (
                        <p key={i} className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                          · {r.product_name_snapshot || r.product_id} × {r.quantity}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Create partial shipment */}
            {currentMode === 'partial_ship' && summary.can_ship_ready && !summary.all_shipped && (
              <div className="rounded-xl border-2 border-emerald-400/60 bg-emerald-50/60 dark:bg-emerald-500/5 p-3 space-y-2" data-testid="ps-create-form">
                <p className="text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-300">부분출고 생성</p>
                <div className="grid grid-cols-2 gap-2">
                  <select value={courier} onChange={(e) => setCourier(e.target.value)}
                    className="px-2 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="ps-courier">
                    {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input value={tracking} onChange={(e) => setTracking(e.target.value)}
                    placeholder="송장번호 입력"
                    className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent font-mono" data-testid="ps-tracking"/>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-gray-500">배송비 (mock)</label>
                  <input type="number" value={feeKrw} onChange={(e) => setFeeKrw(e.target.value)}
                    className="w-24 px-2 py-1 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent" data-testid="ps-fee"/>
                  <span className="text-[11px] text-gray-500">원 · 판매자 부담 · 실 청구 없음</span>
                </div>
                <button onClick={createShipment} disabled={creatingShip || totalSelectedQty <= 0 || !tracking.trim()}
                  className="w-full py-2 rounded-full bg-emerald-600 text-white text-xs font-black disabled:opacity-40"
                  data-testid="ps-create-submit">
                  {creatingShip ? '생성 중…' : `선택 ${totalSelectedQty}개 부분출고 생성`}
                </button>
              </div>
            )}
            {currentMode === 'hold_all' && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 p-3 text-[11px] text-amber-700 dark:text-amber-300" data-testid="ps-hold-notice">
                <Clock size={12} className="inline mr-1"/> 합포 대기 모드 — 준비된 상품이 있어도 출고하지 않습니다. 부분출고를 하려면 위에서 [준비상품 먼저 선발송]을 선택해주세요.
              </div>
            )}
          </div>
        )}
      </div>

      {editItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ps-edit-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditItem(null)}/>
          <div className="relative bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-2xl w-full max-w-md p-4 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm font-black">상품 상태 변경</p>
              <button onClick={() => setEditItem(null)}><X size={14}/></button>
            </div>
            <p className="text-xs text-gray-500 truncate">{editItem.item.name_snapshot}</p>
            <div>
              <p className="text-[10px] font-black uppercase text-gray-500 mb-1">상태</p>
              {/* STEP OMS-PARTIAL-PICK-2A-FIX-R1 · [출고보류] 추가.  재고문제(입고대기/품절)와 구분. */}
              <div className="grid grid-cols-2 gap-1">
                {['ready', 'hold', 'waiting_stock', 'out_of_stock', 'cancelled'].map((s) => (
                  <button key={s} onClick={() => setEditItem({ ...editItem, newStatus: s })}
                    className={`px-2 py-1.5 rounded border text-[11px] font-black ${editItem.newStatus === s ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10'}`}
                    data-testid={`ps-edit-status-${s}`}>
                    {ITEM_STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
              {editItem.newStatus === 'hold' && (
                <p className="mt-1 text-[10px] text-indigo-600 dark:text-indigo-300" data-testid="ps-edit-hold-hint">
                  재고는 있지만 이번 출고에서 보류합니다. 입고대기(재고부족)와 구분됩니다.
                </p>
              )}
            </div>
            {(editItem.newStatus === 'waiting_stock' || editItem.newStatus === 'out_of_stock') && (
              <div>
                <p className="text-[10px] font-black uppercase text-gray-500 mb-1">입고 예정일 (선택)</p>
                <input type="date" value={editItem.eta} onChange={(e) => setEditItem({ ...editItem, eta: e.target.value })}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="ps-edit-eta"/>
              </div>
            )}
            <button onClick={saveItemStatus} className="w-full py-2 rounded-full bg-black text-white dark:bg-white dark:text-black text-xs font-black" data-testid="ps-edit-save">저장</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SumChip({ label, v, cls, tid }) {
  return (
    <div className={`px-2 py-1.5 rounded-lg border text-center ${cls}`} data-testid={tid}>
      <p className="text-[9px] font-black uppercase">{label}</p>
      <p className="text-sm font-black">{v}</p>
    </div>
  );
}
